import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { retrieveDog } from "../api/dog";
import { retrieveAllDogs } from "../api/retrieveAllDogs";
import profile from "../images/temp.svg";
import NotFound from "./NotFound";
import { calculateAge } from "../utils/calculateAge";
import { capitalizeWords } from "../utils/capitalizeWords";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import DonationForm from "../components/DonationForm";
import SuccessPopup from "../components/FosterSuccess/FosterSuccess";
import { generateDogDonation } from "../api/donate";
import { privateDogModel } from "../types/privateDogModel";
import { getNameFromDocument } from "../utils/getNameFromDocument";
import { useGlobal } from "../Context/GlobalContext";
import MoreDogsPopup from "../components/MoreDogsPopup";
import { isEmail } from "../utils/emailUrlCheck";
import { addParagraphs } from "../utils/addParagraphs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowUpRightFromSquare,
  faEnvelope,
  faGlobe,
  faHeart,
  faLocationDot,
  faPhone,
  faShareNodes,
} from "@fortawesome/free-solid-svg-icons";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLIC_KEY as string,
);

const Dog: React.FC = () => {
  const { dog_id } = useParams<{ dog_id: string }>();
  const [dog, setDog] = useState<privateDogModel>();
  const [dogStatus, setDogStatus] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [notFound, setNotFound] = useState<boolean>(false);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [showDonationBox, setShowDonationBox] = useState(false);
  const [email, setEmail] = useState<string>("");
  const [amount, setAmount] = useState<number>(0);
  const [customAmount, setCustomAmount] = useState<number | null>(null);
  const [clientSecret, setClientSecret] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [donationId, setDonationId] = useState<number | null>(null);
  const [showPaymentBox, setShowPaymentBox] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("");
  const [showThankYouMessage, setShowThankYouMessage] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const currentUrl = `${window.location.origin}${location.pathname}`;
  const { authorized } = useGlobal();
  const [showMoreImages, setShowMoreImages] = useState(false);
  const [dogImages, setDogImages] = useState<string[]>([]);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [similarDogs, setSimilarDogs] = useState<privateDogModel[]>([]);

  const placeDogImages = (images: any) => {
    let dogImages: string[] = [];
    images.forEach((image: any) => {
      dogImages.push(image.image);
    });
    setDogImages(dogImages);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    setNotFound(false);
    setSimilarDogs([]);
    const fetchDog = async () => {
      try {
        const response = await retrieveDog(dog_id);
        const tempDog = await setDogData(response);
        preRender(tempDog);
        fetchSimilarDogs(tempDog);
      } catch (error) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchDog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dog_id]);

  const setDogData = (response: any) => {
    const notes = addParagraphs(response.notes);

    const tempDog: privateDogModel = {
      id: response.id,
      dog_id: response.dog_id,
      name: response.name,
      sex: response.sex,
      status: response.status,
      age: response.age,
      weight: response.weight,
      breed: response.breed,
      euth_date: response.euth_date,
      notes: notes,
      adoptable_status: response.adoptable_status,
      active: response.active,
      shelter_name: response.shelter_name,
      rescue_name: response.rescue_name,
      address: {
        country: response.address?.country ?? "",
        state: response.address.state,
        city: response.address.city,
        street: response.address?.street ?? "",
      },
      shelter_phone: response.shelter_phone,
      shelter_email: response.shelter_email,
      images: response.images,
      documents: response.documents,
      rescue_url: response.rescue_url,
      amazon_wishlist_url: response.amazon_wishlist_url,
      chewy_wishlist_url: response.chewy_wishlist_url,

      owner: response.owner,
      collaborators: response.collaborators,
      fosters: response.fosters,
      update_logs: response.update_logs,
    };

    placeDogImages(tempDog.images);
    setDog(tempDog);
    return tempDog;
  };

  // Fetch up to 4 dogs to surface in the "Still looking?" section.
  // Priority: same location (state, same-city first), then same breed,
  // then any other active dog to fill the remaining slots.
  const fetchSimilarDogs = async (current: privateDogModel) => {
    try {
      const data = await retrieveAllDogs();
      const all: any[] = Array.isArray(data) ? data : (data?.dogs ?? []);
      const norm = (s?: string) => (s || "").trim().toLowerCase();
      const curState = norm(current.address?.state);
      const curCity = norm(current.address?.city);
      const curBreed = norm(current.breed);

      const others = all.filter(
        (d) => d && d.active && d.dog_id !== current.dog_id,
      );

      const used = new Set<string>();
      const take = (list: any[]) => {
        list.forEach((d) => used.add(d.dog_id));
        return list;
      };

      const locationMatches = take(
        others
          .filter((d) => curState && norm(d.address?.state) === curState)
          .sort((a, b) => {
            const aCity = norm(a.address?.city) === curCity ? 0 : 1;
            const bCity = norm(b.address?.city) === curCity ? 0 : 1;
            return aCity - bCity;
          }),
      );

      const breedMatches = take(
        others.filter(
          (d) =>
            curBreed && norm(d.breed) === curBreed && !used.has(d.dog_id),
        ),
      );

      const fill = others.filter((d) => !used.has(d.dog_id));

      setSimilarDogs(
        [...locationMatches, ...breedMatches, ...fill].slice(0, 4),
      );
    } catch (error) {
      setSimilarDogs([]);
    }
  };

  const preRender = (dog: privateDogModel) => {
    if (!dog.images || dog.images.length === 0) {
      dog.images = [{ id: 0, image: profile }];
    }
    if (dog.adoptable_status === "at_risk") {
      setDogStatus("at_risk");
    } else if (dog.adoptable_status === "in_rescue_or_foster") {
      setDogStatus("in_rescue/foster");
    }
  };

  const handleShareClick = () => {
    navigator.clipboard.writeText(currentUrl);
    setShowPopup(true);
    setTimeout(() => {
      setShowPopup(false);
    }, 1000);
  };

  const handleDonateClick = async (donationAmount: number) => {
    // Make sure email is valid
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setPaymentStatus("Please enter a valid email.");
      return;
    }
    if (!donationAmount || isNaN(donationAmount)) {
      setPaymentStatus("Please enter a valid amount.");
      return;
    }
    setPaymentStatus("");

    setIsProcessing(true);
    try {
      const { donation_id, client_secret } = await generateDogDonation({
        amount: donationAmount,
        user_email: email,
        type: "Dog",
        dog_id: Number(dog?.id),
      });
      setClientSecret(client_secret);
      setDonationId(donation_id);
      setShowDonationBox(false);
      setShowPaymentBox(true);
    } catch (error) {
      console.error("Error generating donation:", error);
      setPaymentStatus("Error generating donation. Please try again.");
      setIsProcessing(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handleAmountClick = (amount: number) => {
    setAmount(amount);
    handleDonateClick(amount);
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/^0+/, "");
    setCustomAmount(value === "" ? 0 : Number(value));
  };

  const handleCustomAmountSubmit = () => {
    if (customAmount && !isNaN(customAmount)) {
      setAmount(customAmount);
      handleDonateClick(customAmount);
    } else {
      setPaymentStatus("Please enter a valid amount.");
    }
  };
  const checkApproved = () => {
    if (localStorage.getItem("userData")) {
      const userData = JSON.parse(localStorage.getItem("userData") as string);
      if (userData.is_approved) {
        return true;
      }
    }
    return false;
  };
  const formatDateAndTime = (timestamp: string, email: string): string => {
    const date = new Date(timestamp);

    // Format date to MM.DD.YYYY
    const formattedDate = `${date.getMonth() + 1}.${date.getDate()}.${date.getFullYear()}`;

    // Convert to PST timezone
    const options: Intl.DateTimeFormatOptions = {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
      timeZone: "America/Los_Angeles",
    };
    const formattedTime = new Intl.DateTimeFormat("en-US", options).format(
      date,
    );

    return `${email}, ${formattedDate}, ${formattedTime} PST`;
  };
  const { previousUrl } = useGlobal();
  const handleBackNavigation = (event: any) => {
    event.preventDefault();
    // Check previous url to see if it was a pup profile url
    const keyword = "pupprofile";

    if (!previousUrl || !previousUrl.includes(keyword)) {
      navigate("/browse");
    } else {
      navigate(-1);
    }
    window.scrollTo(0, 0);
  };

  const goToDog = (id: string) => {
    navigate(`/browse/${encodeURIComponent(id)}`);
    window.scrollTo(0, 0);
  };

  const isAtRisk = dogStatus === "at_risk";
  const contactName =
    (isAtRisk ? dog?.shelter_name : dog?.rescue_name) ||
    dog?.shelter_name ||
    dog?.rescue_name ||
    "";
  const hasWishlist = !!(dog?.amazon_wishlist_url || dog?.chewy_wishlist_url);

  // A single labelled row in the details table.
  const DetailRow = ({ label, value }: { label: string; value: string }) => (
    <div className="flex w-full items-center gap-3 py-2">
      <p className="w-40 min-w-40 shrink-0 font-semibold text-black">{label}</p>
      <p className="flex-1 text-black">{value}</p>
    </div>
  );

  return (
    <>
      {notFound && <NotFound />}
      {!loading && dog && !dog.active && <NotFound />}
      {!loading && dog && dog.active && (
        <section className="w-full font-poppins text-gray-dark">
          <main className="mx-auto w-full max-w-[1312px] px-6 py-10 md:px-16">
            {/* Back */}
            <button
              onClick={handleBackNavigation}
              className="flex items-center gap-2 font-bold text-black hover:text-blue-strong"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>Back to all dogs</span>
            </button>

            {/* Profile */}
            <div className="mt-8 flex flex-col gap-10 md:flex-row md:gap-16">
              {/* Left column */}
              <div className="flex w-full flex-col gap-6 md:w-[453px] md:shrink-0">
                <img
                  src={dog.images[0].image}
                  alt={dog.images[0].alt ?? dog.name}
                  className="h-[430px] w-full cursor-pointer rounded-xl object-cover"
                  onClick={() => setShowMoreImages(true)}
                />
                {dog.images.length > 1 && (
                  <p
                    className="-mt-3 cursor-pointer text-center text-sm text-blue-strong hover:underline"
                    onClick={() => setShowMoreImages(true)}
                  >
                    Click to view more
                  </p>
                )}

                {/* Location & Contact */}
                <div className="flex flex-col gap-4 rounded-xl bg-blue-pale px-6 pb-6 pt-4">
                  <p className="text-sm font-medium uppercase tracking-[0.42px] text-gray-dark">
                    Location &amp; Contact
                  </p>
                  <p className="break-words text-2xl font-semibold text-black">
                    {contactName}
                  </p>
                  <div className="flex flex-col gap-3">
                    {(dog.address.street ||
                      dog.address.city ||
                      dog.address.state) && (
                      <div className="flex items-center gap-3">
                        <FontAwesomeIcon
                          icon={faLocationDot}
                          className="w-6 text-black"
                        />
                        <p className="flex-1 break-words text-black">
                          {[
                            dog.address.street,
                            dog.address.city,
                            dog.address.state,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                      </div>
                    )}
                    {/* Rescue dogs use a website or email; shelter dogs use email */}
                    {!isAtRisk && dog.rescue_url ? (
                      <div className="flex items-center gap-3">
                        <FontAwesomeIcon
                          icon={isEmail(dog.rescue_url) ? faEnvelope : faGlobe}
                          className="w-6 text-black"
                        />
                        {isEmail(dog.rescue_url) ? (
                          <a
                            href={`mailto:${dog.rescue_url}?subject=Adoption Inquiry for ${dog.name}`}
                            className="min-w-0 flex-1 break-all font-semibold text-blue-strong underline"
                          >
                            {dog.rescue_url}
                          </a>
                        ) : (
                          <a
                            href={dog.rescue_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="min-w-0 flex-1 break-all font-semibold text-blue-strong underline"
                          >
                            {dog.rescue_url}
                          </a>
                        )}
                      </div>
                    ) : (
                      dog.shelter_email && (
                        <div className="flex items-center gap-3">
                          <FontAwesomeIcon
                            icon={faEnvelope}
                            className="w-6 text-black"
                          />
                          <a
                            href={`mailto:${dog.shelter_email}?subject=Adoption Inquiry for ${dog.name}`}
                            className="min-w-0 flex-1 break-all font-semibold text-blue-strong underline"
                          >
                            {dog.shelter_email}
                          </a>
                        </div>
                      )
                    )}
                    {/* Phone is only meaningful for at-risk (shelter) dogs;
                        rescue/foster dogs never have a known phone number */}
                    {isAtRisk && dog.shelter_phone && (
                      <div className="flex items-center gap-3">
                        <FontAwesomeIcon
                          icon={faPhone}
                          className="w-6 text-black"
                        />
                        <a
                          href={`tel:${dog.shelter_phone}`}
                          className="min-w-0 flex-1 break-words font-semibold text-blue-strong"
                        >
                          {dog.shelter_phone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right column */}
              <div className="flex min-w-0 flex-1 flex-col gap-6">
                {/* Title + actions */}
                <div className="flex w-full items-center gap-6">
                  <div className="flex flex-1 items-center gap-3">
                    <h1 className="break-words text-4xl font-extrabold text-blue-strong">
                      {dog.name}
                    </h1>
                    {isAtRisk && (
                      <span className="rounded bg-orange px-2 py-0.5 text-sm font-medium uppercase tracking-[0.42px] text-white">
                        Urgent
                      </span>
                    )}
                  </div>
                  <div className="relative flex items-center gap-3">
                    {!isAtRisk && hasWishlist && (
                      <button
                        type="button"
                        title="Donate to wishlist"
                        onClick={() =>
                          window.open(
                            dog.amazon_wishlist_url || dog.chewy_wishlist_url,
                            "_blank",
                            "noopener,noreferrer",
                          )
                        }
                        className="flex size-10 items-center justify-center rounded-md bg-blue-pale text-blue-strong hover:bg-blue"
                      >
                        <FontAwesomeIcon icon={faHeart} />
                      </button>
                    )}
                    <button
                      type="button"
                      title="Share on social media"
                      onClick={handleShareClick}
                      className="flex size-10 items-center justify-center rounded-md bg-blue-pale text-blue-strong hover:bg-blue"
                    >
                      <FontAwesomeIcon icon={faShareNodes} />
                    </button>
                    <div
                      className={`absolute -top-9 right-0 animate-bounce text-nowrap rounded-md bg-blue-strong px-4 py-1 text-sm text-white ${
                        showPopup ? "block" : "hidden"
                      }`}
                    >
                      Link copied to clipboard
                    </div>
                  </div>
                </div>

                {/* Details table */}
                <div className="flex flex-col rounded-xl">
                  {isAtRisk && (
                    <DetailRow label="ID" value={`#${dog.dog_id}`} />
                  )}
                  <DetailRow label="Sex" value={capitalizeWords(dog.sex)} />
                  <DetailRow label="Age" value={calculateAge(dog.age)} />
                  <DetailRow
                    label="Estimated Breed"
                    value={capitalizeWords(dog.breed)}
                  />
                  <DetailRow label="Weight" value={`${dog.weight} lbs`} />
                  {!isAtRisk && (dog.address.city || dog.address.state) && (
                    <DetailRow
                      label="Location"
                      value={[dog.address.city, dog.address.state]
                        .filter(Boolean)
                        .join(", ")}
                    />
                  )}
                </div>

                {/* Notes */}
                <div className="flex flex-col gap-4">
                  <h2 className="text-2xl font-semibold text-gray-dark">
                    Notes
                  </h2>
                  <div
                    className={`relative overflow-hidden break-words text-black ${
                      notesExpanded ? "" : "max-h-44"
                    }`}
                    dangerouslySetInnerHTML={{ __html: dog.notes }}
                  />
                  {dog.notes && dog.notes.length > 280 && (
                    <button
                      type="button"
                      onClick={() => setNotesExpanded((v) => !v)}
                      className="w-fit font-bold text-blue-strong hover:underline"
                    >
                      {notesExpanded ? "Show Less" : "Show More"}
                    </button>
                  )}
                  {isAtRisk &&
                    (dog.status === "medical" ||
                      dog.status === "behavioral") && (
                      <p className="font-bold italic text-gray1">
                        *This dog is listed as rescue only. If you are
                        interested in adopting, please indicate that you are
                        interested in fostering to adopt in a foster
                        application.
                      </p>
                    )}
                </div>

                {/* Adoption call-to-action */}
                {isAtRisk ? (
                  <div className="flex flex-col gap-4">
                    <h2 className="text-2xl font-semibold text-gray-dark">
                      Do you want to adopt {dog.name}?
                    </h2>
                    <p className="text-black">
                      Call the shelter now to confirm availability, then go to
                      the shelter to adopt!
                    </p>
                    {/* Not able to adopt? */}
                    <div className="flex flex-col gap-4 pt-2">
                      <p className="text-xl font-semibold text-gray-dark">
                        Not able to adopt?
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={() => {
                            navigate(`/foster-interest/${encodeURIComponent(dog_id ?? "")}`);
                            window.scrollTo(0, 0);
                          }}
                          className="flex min-h-[48px] min-w-[152px] items-center justify-center rounded-md bg-blue px-6 font-bold text-white hover:bg-blue-dark"
                          type="button"
                        >
                          Apply to foster
                        </button>
                        <button
                          onClick={() => setShowDonationBox(true)}
                          className="flex min-h-[48px] min-w-[152px] items-center justify-center gap-2 rounded-md bg-orange px-6 font-bold text-white hover:bg-orange-dark"
                          type="button"
                        >
                          <FontAwesomeIcon icon={faHeart} />
                          Donate to support
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    <h2 className="text-2xl font-semibold text-gray-dark">
                      Interested in adopting {dog.name}?
                    </h2>
                    {dog.rescue_name && (
                      <p className="text-black">
                        {dog.name} is available for adoption through{" "}
                        {dog.rescue_name}.
                      </p>
                    )}
                    <div className="flex flex-wrap gap-3 pt-2">
                      {(dog.rescue_url || dog.shelter_email) && (
                        <button
                          onClick={() => {
                            const target = dog.rescue_url || dog.shelter_email;
                            if (isEmail(target)) {
                              window.location.href = `mailto:${target}?subject=Adoption Inquiry for ${dog.name}`;
                            } else {
                              window.open(
                                target,
                                "_blank",
                                "noopener,noreferrer",
                              );
                            }
                          }}
                          className="flex min-h-[48px] min-w-[152px] items-center justify-center rounded-md bg-blue-strong px-6 font-bold text-white hover:bg-blue-dark"
                          type="button"
                        >
                          Apply now
                        </button>
                      )}
                      {dog.amazon_wishlist_url && (
                        <button
                          onClick={() =>
                            window.open(
                              dog.amazon_wishlist_url,
                              "_blank",
                              "noopener,noreferrer",
                            )
                          }
                          className="flex min-h-[48px] min-w-[152px] items-center justify-center gap-2 rounded-md bg-orange px-6 font-bold text-white hover:bg-orange-dark"
                          type="button"
                        >
                          <FontAwesomeIcon icon={faHeart} />
                          Amazon Wishlist
                        </button>
                      )}
                      {dog.chewy_wishlist_url && (
                        <button
                          onClick={() =>
                            window.open(
                              dog.chewy_wishlist_url,
                              "_blank",
                              "noopener,noreferrer",
                            )
                          }
                          className="flex min-h-[48px] min-w-[152px] items-center justify-center gap-2 rounded-md bg-orange px-6 font-bold text-white hover:bg-orange-dark"
                          type="button"
                        >
                          <FontAwesomeIcon icon={faHeart} />
                          Chewy Wishlist
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Update log (approved users) */}
                {authorized &&
                  dog.update_logs &&
                  dog.update_logs.length > 0 && (
                    <p className="pt-2 text-sm text-gray3">
                      Last Update:{" "}
                      {formatDateAndTime(
                        dog.update_logs[0].updated_at,
                        dog.update_logs[0].updated_by,
                      )}
                    </p>
                  )}
              </div>
            </div>

            {/* Donation flow (at-risk dogs) */}
            {isAtRisk && showDonationBox && (
              <section className="mt-12 flex flex-col items-center">
                <div className="mb-4 flex flex-col items-center">
                  <label className="mb-2 text-xl font-semibold text-blue-strong">
                    Email
                  </label>
                  <input
                    className="mb-4 border-2 px-2 py-1"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="Enter your email"
                  />
                </div>
                <p className="mb-4 text-xl font-semibold text-blue-strong">
                  Select an Amount
                </p>
                <div className="mb-4 flex w-[300px] flex-wrap justify-center md:w-full">
                  {[10, 20, 50, 100].map((amount) => (
                    <button
                      key={amount}
                      className="mx-2 mt-2 rounded bg-blue-strong px-4 py-2 font-bold text-white hover:bg-blue-dark"
                      onClick={() => handleAmountClick(amount)}
                      disabled={isProcessing}
                    >
                      ${amount}
                    </button>
                  ))}
                  <button
                    className="mx-2 mt-2 rounded bg-blue-strong px-4 py-2 font-bold text-white hover:bg-blue-dark"
                    onClick={() => setCustomAmount(0)}
                    disabled={isProcessing}
                  >
                    Other
                  </button>
                </div>
                {customAmount !== null && (
                  <div className="mt-4 flex flex-col items-center justify-center gap-2 md:mt-0 md:flex-row md:gap-0">
                    <label className="md:mr-2">Enter Donation amount:</label>
                    <input
                      className="border-2 px-2 py-1"
                      type="number"
                      value={customAmount || ""}
                      onChange={handleCustomAmountChange}
                      min="1"
                    />
                    <button
                      className="rounded bg-blue-strong px-4 py-2 font-semibold text-white hover:bg-blue-dark md:ml-2"
                      onClick={handleCustomAmountSubmit}
                      disabled={isProcessing}
                    >
                      Proceed
                    </button>
                  </div>
                )}
                {paymentStatus && (
                  <p className="mt-2 text-red-500">{paymentStatus}</p>
                )}
              </section>
            )}
            {isAtRisk && showPaymentBox && clientSecret && (
              <>
                <section className="mt-12 flex flex-col items-center">
                  <h2 className="text-2xl font-bold text-blue-strong">
                    Complete your donation of ${amount} to {dog.name}
                  </h2>
                  <p className="mt-2 text-lg">Thank You For Your Support!</p>
                </section>
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <DonationForm
                    clientSecret={clientSecret}
                    donationId={donationId}
                    setShowPaymentBox={setShowPaymentBox}
                    setShowThankYouMessage={setShowThankYouMessage}
                    setPaymentStatus={setPaymentStatus}
                  />
                </Elements>
              </>
            )}
            {showThankYouMessage && (
              <div className="mt-12 flex flex-col items-center">
                <h2 className="text-2xl font-bold text-green-500">
                  Payment Success!
                </h2>
              </div>
            )}
            {showThankYouMessage && (
              <SuccessPopup
                message="Thank you for your donation to Pup Profile! A confirmation will be sent to the email provided."
                onClose={() => setShowThankYouMessage(false)}
              />
            )}

            {/* Documents (approved users) */}
            {authorized && checkApproved() && (
              <div className="mt-10 flex flex-col gap-4 rounded-xl bg-blue-pale p-8">
                <p className="text-lg font-bold text-gray1">Documents</p>
                {dog.documents && dog.documents.length > 0 ? (
                  <ul className="flex flex-col gap-2">
                    {dog.documents.map((document, index) => (
                      <li key={index}>
                        <a
                          href={document.document}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-strong underline"
                        >
                          {getNameFromDocument(document.document)}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No documents available.</p>
                )}
              </div>
            )}
            {authorized && checkApproved() && (
              <div className="mt-4 flex max-h-96 flex-col gap-4 overflow-y-auto rounded-xl bg-blue-pale p-8">
                <p className="text-lg font-bold text-gray1">Update Logs</p>
                {dog.update_logs && dog.update_logs.length > 0 ? (
                  <ul className="flex flex-col gap-2">
                    {dog.update_logs.map((log, index) => (
                      <li key={index}>
                        <p className="text-gray-700">
                          {formatDateAndTime(log.updated_at, log.updated_by)}
                        </p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No update logs available.</p>
                )}
              </div>
            )}
          </main>

          {/* Still looking? */}
          {similarDogs.length > 0 && (
            <section className="w-full bg-blue-pale py-16">
              <div className="mx-auto w-full max-w-[1312px] px-6 md:px-16">
                <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
                  <div className="flex max-w-[900px] flex-col gap-3">
                    <h2 className="text-3xl font-bold text-blue-strong">
                      Still looking?
                    </h2>
                    <p className="text-xl text-black">
                      Browse similar dogs and find the right companion for your
                      home and lifestyle.
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      navigate("/browse");
                      window.scrollTo(0, 0);
                    }}
                    className="flex items-center gap-2 font-bold text-black hover:text-blue-strong"
                  >
                    Browse all dogs
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                  </button>
                </div>

                <div className="mt-12 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
                  {similarDogs.map((d) => (
                    <button
                      key={d.dog_id}
                      onClick={() => goToDog(d.dog_id)}
                      className="flex flex-col overflow-hidden rounded-lg text-left shadow-md transition hover:-translate-y-1"
                    >
                      <img
                        src={d.images?.[0]?.image || profile}
                        alt={d.images?.[0]?.alt ?? d.name}
                        className="h-[295px] w-full bg-gray-light object-cover"
                      />
                      <div className="flex flex-col gap-2 bg-blue-strong px-6 pb-6 pt-4 text-white">
                        <p className="text-2xl font-semibold">{d.name}</p>
                        <div className="flex flex-col gap-1">
                          <p>{capitalizeWords(d.sex)}</p>
                          <p>{capitalizeWords(d.breed)}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </section>
          )}

          {showMoreImages && (
            <MoreDogsPopup
              dogs={dogImages}
              alt={dog.images[0]?.alt ?? dog.name}
              onClose={() => setShowMoreImages(false)}
            />
          )}
        </section>
      )}
    </>
  );
};

export default Dog;
